import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { QuizResult } from '@mentimeter/quiz-types';
import type { QuizChoiceVisualizationTypeRenderOptions } from '@mentimeter/visualizations-react/types/render-options-by-slide-type/quiz-choice';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';
import type { QuizChoicesPresentationProps } from './types';

interface ViewOnlyPresentationProps
  extends QuizChoicesPresentationProps,
    Partial<QuizChoiceVisualizationTypeRenderOptions> {
  isShowingPreviewResults: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  results: QuizResult[];
  shouldAnimate: boolean;
  slidePublicKey: string;
}

export const ViewOnlyPresentation = ({
  title,
  styledTitle,
  slidePublicKey,
  label,
  description,
  choices,
  textSizeOffset,
  shouldAnimate,
  results,
  isShowingPreviewResults,
  CanvasVisualizationArea,
  renderChoiceVisualization,
  renderChoiceLabel,
}: ViewOnlyPresentationProps) => {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  if (isShowingPreviewResults) {
    return (
      <>
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          description={description}
          fontSizeOffset={textSizeOffset}
          showTextSizeDropdown={false}
          hideAIEditingSuggestions
        />
        <CenteringContainer mt={4}>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            // Force re-render when toggling preview results
            // to avoid reusing the same visualization instance, as it's not really designed to be updated after initial render
            key={`${slidePublicKey}-preview`}
            renderChoiceVisualization={renderChoiceVisualization}
            renderChoiceLabel={renderChoiceLabel}
          />
        </CenteringContainer>
      </>
    );
  }

  return (
    <>
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        label={label}
        fontSizeOffset={textSizeOffset}
        description={description}
        showTextSizeDropdown={true}
        hideAIEditingSuggestions
      />
      <ConditionalWrapper width="100%" mt={4} flex="1">
        <CenteringContainer>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            key={slidePublicKey}
            renderChoiceVisualization={renderChoiceVisualization}
            renderChoiceLabel={renderChoiceLabel}
          />
        </CenteringContainer>
      </ConditionalWrapper>
    </>
  );
};
